import {
  ChakraBox,
  ChakraHStack,
  ChakraText,
  ChakraVStack,
  CircularProgress,
  Divider,
  Edit,
  EquipUIFireflyV2Theme,
  EquipUITheme,
  FlagOutline,
} from '@equip.health/ui';

import { OBSERVATION_GOAL_ACTIVITY_TYPE } from '~/lib/constants/patientActivity.constants';
import {
  GoalHistoryChangeType,
  getGoalActivityHistory,
  getGoalHistoryChangeText,
} from '~/lib/util/goals.util';
import { useGetGoalHistoryQuery } from '~/store/services/clinicalData';
import {
  useGetObservationGoalStatusesEnumQuery,
  useGetObservationGoalTypesEnumQuery,
} from '~/store/services/enumerations';

import { formatObservationDate } from '../utils';

type WeightGoalItemActivityProps = {
  patientId: string;
  goalId: string;
};
const { neutral } = EquipUIFireflyV2Theme.colors;

const { label, labelBold } = EquipUITheme.typography;

const WeightGoalItemActivity = ({
  patientId,
  goalId,
}: WeightGoalItemActivityProps) => {
  const {
    data: observationGoalTypes,
    isLoading: isGetObservationGoalTypesLoading,
  } = useGetObservationGoalTypesEnumQuery();

  const {
    data: observationGoalStatuses,
    isLoading: isGetObservationGoalStatusesLoading,
  } = useGetObservationGoalStatusesEnumQuery();

  const TOP_LEVEL_ID = `goal-item-activity__${goalId}`;

  const { isLoading: isHistoryLoading, data: parsedHistoryData } =
    useGetGoalHistoryQuery(
      {
        patientId,
        filterBy: {
          'external-id': goalId,
          'filter-type': OBSERVATION_GOAL_ACTIVITY_TYPE,
        },
      },
      {
        selectFromResult: ({ data, isLoading, status }) => {
          const parsedHistoryData = getGoalActivityHistory(
            (data ?? []).filter(
              ({ operator }: PatientActivityListObject) =>
                operator !== GoalHistoryChangeType.NEW_FEATURE,
            ),
          ).sort((a: GoalActivityType, b: GoalActivityType) =>
            b.updatedAt.localeCompare(a.updatedAt),
          );
          return {
            data: parsedHistoryData,
            isLoading,
            status,
          };
        },
      },
    );

  return (
    <ChakraBox marginTop="24.5px">
      <Divider id={`${TOP_LEVEL_ID}__divider`} marginBottom="24px" />
      <ChakraText
        {...labelBold}
        color="neutral.primary"
        id={TOP_LEVEL_ID}
        lineHeight="16.8px"
        marginBottom="8px"
      >
        Activity
      </ChakraText>
      {isHistoryLoading ||
      isGetObservationGoalStatusesLoading ||
      isGetObservationGoalTypesLoading ? (
        <CircularProgress
          id={`${TOP_LEVEL_ID}-loading`}
          isIndeterminate
          size="20px"
        />
      ) : (
        <ChakraVStack align="flex-start" spacing="8px">
          {parsedHistoryData.map((activity: GoalActivityType) => {
            const id = `${TOP_LEVEL_ID}__${activity.fieldChanged}__${activity.newValue}__${activity.activityExternalId}`;
            return (
              <ChakraHStack key={id} spacing="16px">
                {activity.type === GoalHistoryChangeType.CREATED ? (
                  <FlagOutline
                    fill={neutral.tertiary}
                    height="16px"
                    width="16px"
                  />
                ) : (
                  <Edit fill={neutral.tertiary} height="16px" width="16px" />
                )}
                <ChakraText
                  {...labelBold}
                  color="neutral.primary"
                  id={`${id}__change-description`}
                >
                  {getGoalHistoryChangeText(
                    activity,
                    observationGoalStatuses,
                    observationGoalTypes,
                  )}
                </ChakraText>
                <ChakraText
                  {...label}
                  color="neutral.secondary"
                  id={`${id}__updated-by`}
                >
                  by
                  <ChakraText as="span" fontWeight="700" marginLeft="4px">
                    {activity.updatedBy}
                  </ChakraText>
                </ChakraText>
                <ChakraText
                  {...label}
                  color="neutral.secondary"
                  id={`${id}__updated-at`}
                >
                  {formatObservationDate(activity.updatedAt)}
                </ChakraText>
              </ChakraHStack>
            );
          })}
        </ChakraVStack>
      )}
    </ChakraBox>
  );
};

export default WeightGoalItemActivity;
